import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  AssignConversationRequest,
  ConversationDetail,
  ConvertConversationRequest,
  CreateChatDraftOrderRequest,
  SendStaffChatMessageRequest,
  UpdateConversationRequest,
} from '@nucleus-commerce/api-client/admin';

import { conversationsApi } from '../api/conversations-api';
import { lastMessageId, mergeIntoThread, type ThreadData } from '../lib/thread-cache';

import { conversationKeys } from './conversation-keys';

/**
 * Pulls the thread rows written since the last one held: actions add event rows (assigned,
 * closed, tagged) that the socket also delivers, and merging by id makes the two harmless.
 */
export async function catchUpThread(
  queryClient: QueryClient,
  storeId: string,
  id: string,
): Promise<void> {
  const key = conversationKeys.messages(storeId, id);
  const after = lastMessageId(queryClient.getQueryData<ThreadData>(key));
  if (!after) return;
  const page = await conversationsApi
    .messages(storeId, id, { after, limit: 100 })
    .catch(() => null);
  if (page) queryClient.setQueryData<ThreadData>(key, (data) => mergeIntoThread(data, page.items));
}

/** Every conversation action answers with the detail: store it, refresh lists and counts. */
function remember(queryClient: QueryClient, conversation: ConversationDetail) {
  queryClient.setQueryData(
    conversationKeys.detail(conversation.storeId, conversation.id),
    conversation,
  );
  void catchUpThread(queryClient, conversation.storeId, conversation.id);
  void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
  return queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
}

/** A reply or a whisper. The composer keeps the draft when it fails (409 closed is toasted). */
export function useSendMessage(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SendStaffChatMessageRequest) => conversationsApi.send(storeId, id, body),
    onSuccess: (message) => {
      queryClient.setQueryData<ThreadData>(conversationKeys.messages(storeId, id), (data) =>
        mergeIntoThread(data, [message]),
      );
      // A reply moves open to pending and may claim the conversation.
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(storeId, id),
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
      return queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

export function useAssignConversation(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignConversationRequest) => conversationsApi.assign(storeId, id, body),
    meta: { successMessage: 'conversations:toasts.assigned' },
    onSuccess: (conversation) => remember(queryClient, conversation),
  });
}

export function useUpdateConversation(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateConversationRequest) => conversationsApi.update(storeId, id, body),
    onSuccess: (conversation) => remember(queryClient, conversation),
  });
}

/** The dialog shows field errors itself, so the global toast stays quiet. */
export function useConvertConversation(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConvertConversationRequest) => conversationsApi.convert(storeId, id, body),
    meta: { successMessage: 'conversations:toasts.converted', silentError: true },
    onSuccess: (conversation) => remember(queryClient, conversation),
  });
}

type Action = 'join' | 'leave' | 'close' | 'reopen';

const ACTION_TOASTS: Record<Action, string> = {
  join: 'conversations:toasts.joined',
  leave: 'conversations:toasts.left',
  close: 'conversations:toasts.closed',
  reopen: 'conversations:toasts.reopened',
};

export function useConversationAction(storeId: string, id: string, action: Action) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conversationsApi[action](storeId, id),
    meta: { successMessage: ACTION_TOASTS[action] },
    onSuccess: (conversation) => remember(queryClient, conversation),
  });
}

/** It leaves the inbox at once; the caller navigates away. */
export function useTrashConversation(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conversationsApi.trash(storeId, id),
    meta: { successMessage: 'conversations:toasts.trashed' },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: conversationKeys.detail(storeId, id) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
      return queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

/** Read receipts over REST while the socket is down; failures are not worth a toast. */
export function useMarkConversationRead(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (upToMessageId: string) => conversationsApi.markRead(storeId, id, upToMessageId),
    meta: { silentError: true },
    onSuccess: (conversation) => {
      queryClient.setQueryData(conversationKeys.detail(storeId, id), conversation);
      void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
      return queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

export function useUploadAttachment(storeId: string, id: string) {
  return useMutation({
    mutationFn: (file: File) => conversationsApi.upload(storeId, id, file),
  });
}

/** A single-use code from the store's discounts (LC12); the allowance is refetched after. */
export function useIssueDiscount(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (discountId: string) => conversationsApi.issueDiscount(storeId, id, discountId),
    onSuccess: (message) => {
      queryClient.setQueryData<ThreadData>(conversationKeys.messages(storeId, id), (data) =>
        mergeIntoThread(data, [message]),
      );
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(storeId, id),
        exact: true,
      });
      return queryClient.invalidateQueries({ queryKey: conversationKeys.discounts(storeId) });
    },
  });
}

/** A draft order quote from the thread (LC12); the conversation remembers it as its latest. */
export function useCreateChatDraftOrder(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChatDraftOrderRequest) =>
      conversationsApi.draftOrder(storeId, id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(storeId, id),
        exact: true,
      });
      await catchUpThread(queryClient, storeId, id);
    },
  });
}

/** Emails the latest quote's invoice and posts the invoice card. */
export function useSendChatInvoice(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conversationsApi.sendInvoice(storeId, id),
    onSuccess: (message) => {
      queryClient.setQueryData<ThreadData>(conversationKeys.messages(storeId, id), (data) =>
        mergeIntoThread(data, [message]),
      );
      return queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(storeId, id),
        exact: true,
      });
    },
  });
}
